import { useCallback, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useFullscreen } from "../../contexts/FullscreenContext";
import styles from "./ZenModeToggle.module.css";

export interface ZenModeToggleProps {
  keyboardShortcut?: string;
}

export function ZenModeToggle({ keyboardShortcut = "Mod+Shift+Z" }: ZenModeToggleProps) {
  const { isFullscreen, toggleFullscreen, exitFullscreen, isFullscreenSupported } = useFullscreen();
  const prevFullscreen = useRef(isFullscreen);

  const handleToggle = useCallback(() => {
    toggleFullscreen();
  }, [toggleFullscreen]);

  // Show toast when entering zen mode
  useEffect(() => {
    if (isFullscreen && !prevFullscreen.current) {
      toast("Zen Mode — Esc to exit · Alt+1/2/3 to switch views", {
        duration: 3000,
        icon: "🧘",
        position: "bottom-center",
      });
    }
    prevFullscreen.current = isFullscreen;
  }, [isFullscreen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Mod+Shift+Z toggles zen mode
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && e.shiftKey && e.key.toLowerCase() === "z") {
        e.preventDefault();
        handleToggle();
        return;
      }

      // Esc exits zen mode when active
      if (e.key === "Escape" && isFullscreen) {
        e.preventDefault();
        exitFullscreen();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleToggle, isFullscreen, exitFullscreen]);

  if (!isFullscreenSupported) {
    return null;
  }

  return (
    <button
      type="button"
      className={`${styles.toggle} ${isFullscreen ? styles.toggleActive : ""}`}
      onClick={handleToggle}
      title={`Zen Mode ${isFullscreen ? "On" : "Off"} (${keyboardShortcut.replace("Mod", "⌘/Ctrl")})`}
      aria-pressed={isFullscreen}
    >
      <ZenIcon />
      <span className={styles.label}>Zen</span>
      {isFullscreen && <span className={styles.indicator} />}
    </button>
  );
}

function ZenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 5V2H5M11 2H14V5M14 11V14H11M5 14H2V11"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
